/**
 * ChatModule -- Full AI Chat assistant.
 *
 * Premium chat interface with:
 * - Streaming BOS responses via SSE
 * - Client-side intent detection with rich cards
 * - IndexedDB conversation persistence
 * - Action buttons, preview cards, suggested prompts
 * - Offline-capable local intent handling
 * - Conversation history side panel
 */

import type { AweniContext } from '../../context/AweniContext';
import { NativeService } from '../../services/NativeService';

// ── Types ─────────────────────────────────────────────────

export interface ActionButton {
  label: string;
  action: 'navigate' | 'api_call' | 'copy' | 'share';
  target: string;
  icon?: string;
}

export interface PreviewCard {
  type: 'game' | 'article' | 'prayer' | 'expense' | 'stats';
  title: string;
  subtitle?: string;
  image?: string;
  action?: ActionButton;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  metadata?: {
    actions?: ActionButton[];
    cards?: PreviewCard[];
    module?: string;
    intent?: string;
    streaming?: boolean;
  };
}

interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: number;
  updatedAt: number;
}

interface IntentMatch {
  intent: string;
  cards: PreviewCard[];
  actions: ActionButton[];
  localResponse: string;
}

// ── Constants ─────────────────────────────────────────────

const DB_NAME = 'aweni_chat_history';
const DB_VERSION = 1;
const STORE_NAME = 'conversations';
const BOS_CHAT_ENDPOINT = '/aweni/api/v1/chat';
const MAX_HISTORY_DISPLAY = 20;

const SUGGESTED_PROMPTS = [
  { key: 'chat_suggested_study', icon: '\uD83D\uDCDA' },
  { key: 'chat_suggested_prayer', icon: '\uD83D\uDD4C' },
  { key: 'chat_suggested_streaks', icon: '\uD83D\uDD25' },
  { key: 'chat_suggested_game', icon: '\uD83C\uDFAE' },
  { key: 'chat_suggested_expense', icon: '\uD83D\uDCB0' },
  { key: 'chat_suggested_verse', icon: '\u2728' },
  { key: 'chat_suggested_community', icon: '\uD83D\uDC65' },
];

// ── Intent Patterns ───────────────────────────────────────

const INTENT_PATTERNS: Array<{
  intent: string;
  patterns: RegExp[];
  cardType: PreviewCard['type'];
  module: string;
}> = [
  {
    intent: 'prayer',
    patterns: [
      /\b(prayer|salat|salah|fajr|dhuhr|zuhr|asr|maghrib|isha|priere|prière|gebet|oración|صلا[ةه]|فجر|ظهر|عصر|مغرب|عشاء)\b/i,
    ],
    cardType: 'prayer',
    module: 'memoriz',
  },
  {
    intent: 'game',
    patterns: [/\b(game|play|math|logic|jeu|jouer|spiel|spielen|juego|jugar|لعب|ألعاب)\b/i],
    cardType: 'game',
    module: 'games',
  },
  {
    intent: 'memoriz',
    patterns: [
      /\b(card|review|due|memoriz|quran|coran|karte|carte|revision|révision|tarjeta|بطاق|مراجع|قرآن)\b/i,
    ],
    cardType: 'stats',
    module: 'memoriz',
  },
  {
    intent: 'expense',
    patterns: [
      /\b(expense|owe|balance|money|debt|argent|dette|geld|schuld|dinero|deuda|مال|دين|مصروف)\b/i,
    ],
    cardType: 'expense',
    module: 'balancetab',
  },
  {
    intent: 'article',
    patterns: [/\b(article|read|blog|lire|lesen|leer|مقال|قراءة)\b/i],
    cardType: 'article',
    module: 'blog',
  },
];

// ── IndexedDB Helpers ─────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveConversation(conv: Conversation): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(conv);
    await new Promise<void>((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
    db.close();
  } catch {
    // Silently fail -- chat still works without persistence
  }
}

async function loadConversations(): Promise<Conversation[]> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const all = await new Promise<Conversation[]>((res, rej) => {
      const req = store.index('updatedAt').getAll();
      req.onsuccess = () => res(req.result);
      req.onerror = () => rej(req.error);
    });
    db.close();
    return all.sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

async function deleteConversation(id: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    await new Promise<void>((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
    db.close();
  } catch {
    // silent
  }
}

// ── Main Module Class ─────────────────────────────────────

export default class ChatModule {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;
  private conversation: Conversation | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private messagesEl: HTMLElement | null = null;
  private suggestionsEl: HTMLElement | null = null;
  private historyPanelEl: HTMLElement | null = null;
  private typingEl: HTMLElement | null = null;
  private abortController: AbortController | null = null;
  private _thinkingBubbleEl: HTMLElement | null = null;
  private conversations: Conversation[] = [];
  private isStreaming = false;
  private _historyOpen = false;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.ctx = ctx;

    // Lock the surrounding scroll context to the chat. The shell's
    // .module-viewport normally scrolls the WHOLE page; here we want the
    // 3-zone (sticky header / scrollable messages / sticky composer)
    // pattern, so the only thing that scrolls is #chat-messages. Without
    // these overrides the page scrolls past the input bar and the user
    // has to chase the input back into view (the bug reported on prod).
    // Restored in unmount() so other modules keep their normal scroll.
    const viewport = container.closest('.module-viewport') as HTMLElement | null;
    if (viewport) {
      this._viewportPriorOverflow = viewport.style.overflow;
      this._viewportPriorPaddingTop = viewport.style.paddingTop;
      this._viewportPriorPaddingBottom = viewport.style.paddingBottom;
      viewport.style.overflow = 'hidden';
      // Keep the topbar/bottomnav clearance — chat fills exactly the inner
      // box, no more no less. Using 100% on a child of an overflow:hidden
      // padded box is the simplest cross-browser way to grab the inner
      // content area without resorting to dvh tricks (dvh is great when
      // it works but iOS 26 has a known offsetTop bug — see Apple
      // developer forums).
    }
    container.style.height = '100%';

    // Load conversation history
    this.conversations = await loadConversations();

    // Resume last conversation or start new one
    if (this.conversations.length > 0) {
      this.conversation = this.conversations[0];
    } else {
      this.conversation = this._createNewConversation();
    }

    this._render();
    this._bindEvents();
    this._scrollToBottom(false);
  }

  private _viewportPriorOverflow = '';
  private _viewportPriorPaddingTop = '';
  private _viewportPriorPaddingBottom = '';

  unmount(): void {
    this._cancelStreaming();
    // Restore the shell viewport's scroll behavior for the next module.
    if (this.container) {
      const viewport = this.container.closest('.module-viewport') as HTMLElement | null;
      if (viewport) {
        viewport.style.overflow = this._viewportPriorOverflow;
        viewport.style.paddingTop = this._viewportPriorPaddingTop;
        viewport.style.paddingBottom = this._viewportPriorPaddingBottom;
      }
      this.container.style.height = '';
      this.container.innerHTML = '';
    }
    this.container = null;
    this.ctx = null;
    this.conversation = null;
    this.inputEl = null;
    this.messagesEl = null;
    this.suggestionsEl = null;
    this.historyPanelEl = null;
    this.typingEl = null;
  }

  // ── Rendering ──────────────────────────────────────────

  private _render(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const dir = isRtl ? 'rtl' : 'ltr';

    this.container.innerHTML = `
      <div class="chat-module" dir="${dir}" style="
        display:flex; flex-direction:column;
        height:100%; max-width:720px; margin:0 auto;
        position:relative; overflow:hidden;
      ">
        <!-- History side panel -->
        <div id="chat-history-overlay" style="
          position:absolute; inset:0; z-index:20;
          background:rgba(0,0,0,0.4); display:none;
          transition:opacity var(--motion-duration-base) var(--motion-easing-default);
        "></div>
        <div id="chat-history-panel" style="
          position:absolute; top:0; ${isRtl ? 'right' : 'left'}:0; bottom:0;
          width:280px; max-width:80%; z-index:25;
          background:var(--bg-primary); border-${isRtl ? 'left' : 'right'}:1px solid var(--border);
          transform:translateX(${
            isRtl ? '100%' : '-100%'
          }); transition:transform var(--motion-duration-slow) var(--motion-easing-emphasized);
          display:flex; flex-direction:column; overflow:hidden;
        ">
          <div style="padding:16px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between;">
            <span style="font-weight:600; font-size:15px; color:var(--text-primary);">${t(
              'chat_history',
            )}</span>
            <button id="chat-history-close" style="
              width:32px; height:32px; border-radius:8px; display:flex;
              align-items:center; justify-content:center;
              background:var(--card-bg); color:var(--text-secondary); border:none; cursor:pointer;
            " aria-label="${t('common_close')}">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div id="chat-history-list" style="flex:1; overflow-y:auto; padding:8px;"></div>
        </div>

        <!-- Header -->
        <div style="
          padding:12px 16px; display:flex; align-items:center; gap:12px;
          border-bottom:1px solid var(--border); flex-shrink:0;
          background:var(--bg-primary);
        ">
          <button id="chat-btn-history" style="
            width:36px; height:36px; border-radius:10px; display:flex;
            align-items:center; justify-content:center; flex-shrink:0;
            background:var(--card-bg); color:var(--text-secondary);
            border:1px solid var(--border); cursor:pointer;
            transition:all var(--motion-duration-fast) var(--motion-easing-default);
          " aria-label="${t('chat_history')}">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </button>
          <div style="flex:1; min-width:0;">
            <div style="display:flex; align-items:center; gap:8px;">
              <!-- "Online" status dot — uses --accent so it inverts per theme
                   (was hardcoded #0A0A0A which read as black-on-black in dark). -->
              <div style="width:8px; height:8px; border-radius:50%; background:var(--accent); flex-shrink:0;"></div>
              <h1 style="font-size:15px; font-weight:700; color:var(--text-primary); margin:0;">${t(
                'chat_title',
              )}</h1>
            </div>
            <p style="font-size:12px; color:var(--text-secondary); margin:2px 0 0 16px;">${t(
              'chat_subtitle',
            )}</p>
          </div>
          <!-- New conversation "+" button — was background:#0A0A0A18 + color:#0A0A0A
               which gave a 9% alpha black on cream in light (visible faint pill)
               but black-on-black in dark (invisible). Switched to color-mix on
               --accent so the tinted background pairs with --accent text in
               both themes. color-mix is supported in all modern browsers
               (Safari 16.4+, Chrome 111+, Firefox 113+). -->
          <button id="chat-btn-new" style="
            width:36px; height:36px; border-radius:10px; display:flex;
            align-items:center; justify-content:center; flex-shrink:0;
            background:color-mix(in srgb, var(--accent) 12%, transparent); color:var(--accent);
            border:1px solid color-mix(in srgb, var(--accent) 25%, transparent);
            cursor:pointer; transition:all var(--motion-duration-fast) var(--motion-easing-default);
          " aria-label="${t('chat_new_conversation')}">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
        </div>

        <!-- Messages — the ONLY scrolling area inside chat. role=log + aria-live
             is the canonical pattern so screen readers announce new turns
             (per W3C Patterns for Accessible Webchats / MDN ARIA live regions).
             overscroll-behavior:contain prevents reach-the-end bounces from
             scrolling the page underneath. -->
        <div id="chat-messages" role="log" aria-live="polite" aria-relevant="additions text" aria-atomic="false" style="
          flex:1 1 0; min-height:0; overflow-y:auto; padding:16px; display:flex;
          flex-direction:column; gap:12px;
          scroll-behavior:smooth; overscroll-behavior:contain;
          -webkit-overflow-scrolling:touch;
        "></div>

        <!-- Typing indicator -->
        <div id="chat-typing" style="
          display:none; padding:0 16px 8px;
        ">
          <div style="
            display:inline-flex; align-items:center; gap:8px;
            padding:10px 16px; border-radius:16px 16px 16px 4px;
            background:var(--card-bg); box-shadow:var(--shadow-sm);
          ">
            <div class="chat-typing-dots" style="display:flex; gap:4px;">
              <span style="width:6px;height:6px;border-radius:50%;background:var(--text-secondary);animation:chat-dot-bounce var(--motion-duration-deliberate) infinite var(--motion-easing-default) both;animation-delay:var(--motion-duration-instant);"></span>
              <span style="width:6px;height:6px;border-radius:50%;background:var(--text-secondary);animation:chat-dot-bounce var(--motion-duration-deliberate) infinite var(--motion-easing-default) both;animation-delay:var(--motion-duration-fast);"></span>
              <span style="width:6px;height:6px;border-radius:50%;background:var(--text-secondary);animation:chat-dot-bounce var(--motion-duration-deliberate) infinite var(--motion-easing-default) both;animation-delay:var(--motion-duration-slow);"></span>
            </div>
            <span style="font-size:12px; color:var(--text-secondary);">${t('chat_typing')}</span>
          </div>
        </div>

        <!-- Suggested prompts -->
        <div id="chat-suggestions" style="padding:0 16px 8px; flex-shrink:0;">
          <div style="
            display:flex; gap:8px; overflow-x:auto; padding-bottom:4px;
            -webkit-overflow-scrolling:touch; scrollbar-width:none;
          ">
            ${SUGGESTED_PROMPTS.map(
              (p) => `
              <button class="chat-suggest" data-key="${p.key}" style="
                flex-shrink:0; display:flex; align-items:center; gap:6px;
                padding:8px 14px; border-radius:20px; font-size:13px; font-weight:500;
                background:var(--card-bg); color:var(--text-primary);
                border:1px solid var(--border); cursor:pointer;
                transition:all var(--motion-duration-base) var(--motion-easing-default); white-space:nowrap;
              ">
                <span style="font-size:16px;">${p.icon}</span>
                <span>${t(p.key)}</span>
              </button>
            `,
            ).join('')}
          </div>
        </div>

        <!-- Input bar -->
        <div style="
          padding:8px 16px 16px; border-top:1px solid var(--border);
          flex-shrink:0; background:var(--bg-primary);
        ">
          <div style="display:flex; align-items:flex-end; gap:8px;">
            <div style="
              flex:1; display:flex; align-items:flex-end;
              background:var(--card-bg); border:1px solid var(--border);
              border-radius:24px; padding:4px 4px 4px 16px;
              transition:border-color var(--motion-duration-base) var(--motion-easing-default);
            " id="chat-input-wrap">
              <textarea id="chat-input" rows="1" style="
                flex:1; border:none; background:transparent; color:var(--text-primary);
                font-size:14px; line-height:20px; padding:8px 0; resize:none;
                outline:none; max-height:120px; font-family:inherit;
              " placeholder="${t('chat_placeholder')}" aria-label="${t(
      'chat_placeholder',
    )}"></textarea>
              <button id="chat-btn-voice" style="
                width:36px; height:36px; border-radius:50%; flex-shrink:0;
                display:flex; align-items:center; justify-content:center;
                background:transparent; color:var(--text-secondary);
                border:none; cursor:pointer; opacity:0.5;
                transition:all var(--motion-duration-base) var(--motion-easing-default);
              " aria-label="${t('chat_voice_coming_soon')}">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              </button>
            </div>
            <button id="chat-btn-send" style="
              width:44px; height:44px; border-radius:50%; flex-shrink:0;
              display:flex; align-items:center; justify-content:center;
              background:var(--accent); color:var(--accent-text); border:none; cursor:pointer;
              transition:all var(--motion-duration-base) var(--motion-easing-default); transform:scale(1);
            " aria-label="${t('chat_send')}">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="${
                isRtl ? 'chat-rtl-flip' : ''
              }"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>
        </div>
      </div>

      <style>
        @keyframes chat-dot-bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes aweni-thinking-dots {
          0%, 80%, 100% { transform: scale(0.6) translateY(0); opacity: 0.4; }
          40% { transform: scale(1) translateY(-3px); opacity: 1; }
        }
        .aweni-thinking-dots { display:inline-flex; align-items:center; gap:4px; }
        .aweni-thinking-dots span { width:7px; height:7px; border-radius:50%; background:var(--text-secondary); animation: aweni-thinking-dots var(--motion-duration-deliberate) infinite; }
        .aweni-thinking-dots span:nth-child(2) { animation-delay:var(--motion-duration-base); }
        .aweni-thinking-dots span:nth-child(3) { animation-delay:var(--motion-duration-slow); }
        .chat-msg-text-fadein { animation: chat-fade-in var(--motion-duration-base) var(--motion-easing-default) forwards; }
        @keyframes chat-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes chat-card-appear {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .chat-rtl-flip { transform: scaleX(-1); }
        .chat-suggest:hover { border-color: var(--accent) !important; background: color-mix(in srgb, var(--accent) 10%, transparent) !important; }
        .chat-suggest:active { transform: scale(0.96); }
        #chat-input-wrap:focus-within { border-color: var(--accent) !important; }
        #chat-btn-send:active { transform: scale(0.9); }
        #chat-btn-send:hover { filter: brightness(1.1); }
        #chat-btn-history:hover, #chat-btn-new:hover { background: color-mix(in srgb, var(--accent) 15%, transparent) !important; }
        #chat-messages::-webkit-scrollbar { width: 4px; }
        #chat-messages::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
        #chat-history-list::-webkit-scrollbar { width: 3px; }
        #chat-history-list::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
        .chat-action-btn { cursor: pointer; transition: all var(--motion-duration-fast) var(--motion-easing-default); }
        .chat-action-btn:hover { filter: brightness(1.05); transform: translateY(-1px); }
        .chat-action-btn:active { transform: scale(0.97); }
        .chat-card { transition: all var(--motion-duration-base) var(--motion-easing-default); }
        .chat-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
        .chat-history-item { cursor: pointer; transition: background var(--motion-duration-fast) var(--motion-easing-default); }
        .chat-history-item:hover { background: color-mix(in srgb, var(--accent) 10%, transparent) !important; }
      </style>
    `;

    // Cache refs
    this.messagesEl = this.container!.querySelector('#chat-messages');
    this.inputEl = this.container!.querySelector('#chat-input');
    this.suggestionsEl = this.container!.querySelector('#chat-suggestions');
    this.typingEl = this.container!.querySelector('#chat-typing');
    this.historyPanelEl = this.container!.querySelector('#chat-history-panel');

    // Render messages
    this._renderMessages();

    // Show suggestions only if conversation has just the welcome message
    if (this.conversation && this.conversation.messages.length <= 1) {
      this.suggestionsEl!.style.display = '';
    } else {
      this.suggestionsEl!.style.display = 'none';
    }
  }

  private _bindEvents(): void {
    if (!this.container || !this.ctx) return;

    // Send button
    this.container
      .querySelector('#chat-btn-send')
      ?.addEventListener('click', () => this._handleSend());

    // Enter to send
    this.inputEl?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this._handleSend();
      }
    });

    // Auto-resize textarea
    this.inputEl?.addEventListener('input', () => this._autoResize());

    // Suggested prompts
    this.container.querySelectorAll<HTMLButtonElement>('.chat-suggest').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key || '';
        const text = this.ctx!.i18n.t(key);
        if (this.inputEl) {
          this.inputEl.value = text;
          this._autoResize();
        }
        this._handleSend();
      });
    });

    // New conversation
    this.container
      .querySelector('#chat-btn-new')
      ?.addEventListener('click', () => this._startNewConversation());

    // History panel
    this.container
      .querySelector('#chat-btn-history')
      ?.addEventListener('click', () => this._toggleHistory());
    this.container
      .querySelector('#chat-history-overlay')
      ?.addEventListener('click', () => this._closeHistory());
    this.container
      .querySelector('#chat-history-close')
      ?.addEventListener('click', () => this._closeHistory());

    // Voice button
    this.container.querySelector('#chat-btn-voice')?.addEventListener('click', () => {
      this.ctx?.navigate('chat');
      // Import and trigger voice input
      import('./VoiceInput').then((mod) => {
        const vi = new mod.default();
        vi.startListening(
          this.ctx!.i18n.locale,
          (text: string) => {
            if (this.inputEl && text) {
              this.inputEl.value = text;
              this._autoResize();
              this.inputEl.focus();
            }
          },
          () => {
            // Show coming soon toast if not supported
            this._showToast(this.ctx!.i18n.t('chat_voice_coming_soon'));
          },
        );
      });
    });

    // Delegate action button clicks
    this.messagesEl?.addEventListener('click', (e) => {
      const actionBtn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (actionBtn) {
        const action = actionBtn.dataset.action;
        const target = actionBtn.dataset.target || '';
        this._handleAction(action || '', target);
      }
    });
  }

  // ── Message Handling ───────────────────────────────────

  private async _handleSend(): Promise<void> {
    if (!this.inputEl || !this.ctx || !this.conversation || this.isStreaming) return;
    const text = this.inputEl.value.trim();
    if (!text) return;

    this.inputEl.value = '';
    this._autoResize();

    // Hide suggestions after first message
    if (this.suggestionsEl) this.suggestionsEl.style.display = 'none';

    // Add user message
    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text,
      timestamp: Date.now(),
    };
    this.conversation.messages.push(userMsg);
    this.conversation.updatedAt = Date.now();
    this._appendMessage(userMsg);
    this._scrollToBottom();

    // Update title from first user message
    if (this.conversation.messages.filter((m) => m.role === 'user').length === 1) {
      this.conversation.title = text.slice(0, 50) + (text.length > 50 ? '...' : '');
    }

    // Try local intent detection
    const intentMatch = this._detectIntent(text);

    if (intentMatch && this.ctx.offline && !this.ctx.offline.isOnline) {
      // Offline: use local response
      this._respondWithIntent(intentMatch);
      return;
    }

    if (intentMatch) {
      // Show intent card immediately, then also query BOS for richer response
      this._respondWithIntent(intentMatch);

      // If online, also get BOS response for additional context
      if (this.ctx.offline?.isOnline !== false) {
        await this._queryBOS(text, intentMatch.intent);
      }
    } else if (this.ctx.offline?.isOnline === false) {
      // Offline and no intent match
      const offlineMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: this.ctx.i18n.t('chat_offline_message'),
        timestamp: Date.now(),
        metadata: {
          actions: [
            {
              label: this.ctx.i18n.t('nav_games'),
              action: 'navigate',
              target: 'games',
              icon: '\uD83C\uDFAE',
            },
            {
              label: this.ctx.i18n.t('nav_quran'),
              action: 'navigate',
              target: 'memoriz',
              icon: '\uD83D\uDCDA',
            },
          ],
        },
      };
      this.conversation.messages.push(offlineMsg);
      this.conversation.updatedAt = Date.now();
      this._appendMessage(offlineMsg);
      this._scrollToBottom();
      await saveConversation(this.conversation);
    } else {
      // No intent matched, send to BOS
      await this._queryBOS(text);
    }
  }

  private _detectIntent(text: string): IntentMatch | null {
    if (!this.ctx) return null;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const lower = text.toLowerCase();

    for (const ip of INTENT_PATTERNS) {
      for (const pattern of ip.patterns) {
        if (pattern.test(lower)) {
          const cards: PreviewCard[] = [];
          const actions: ActionButton[] = [];
          let localResponse = '';

          switch (ip.intent) {
            case 'prayer':
              cards.push({
                type: 'prayer',
                title: t('chat_card_prayer_title'),
                subtitle: t('chat_card_prayer_subtitle'),
                action: {
                  label: t('chat_card_prayer_action'),
                  action: 'navigate',
                  target: 'memoriz',
                },
              });
              localResponse = t('chat_response_prayer');
              actions.push({
                label: t('memoriz_prayer_times'),
                action: 'navigate',
                target: 'memoriz',
                icon: '\uD83D\uDD4C',
              });
              break;

            case 'game':
              cards.push({
                type: 'game',
                title: t('chat_card_game_title'),
                subtitle: t('chat_card_game_subtitle'),
                action: { label: t('chat_card_game_action'), action: 'navigate', target: 'games' },
              });
              localResponse = t('chat_response_games');
              actions.push({
                label: t('home_play_game'),
                action: 'navigate',
                target: 'games',
                icon: '\uD83C\uDFAE',
              });
              break;

            case 'memoriz':
              cards.push({
                type: 'stats',
                title: t('chat_card_review_title'),
                subtitle: t('chat_card_review_subtitle'),
                action: { label: t('memoriz_start_review'), action: 'navigate', target: 'memoriz' },
              });
              localResponse = t('chat_response_cards');
              actions.push({
                label: t('memoriz_start_review'),
                action: 'navigate',
                target: 'memoriz',
                icon: '\uD83D\uDCDA',
              });
              break;

            case 'expense':
              cards.push({
                type: 'expense',
                title: t('chat_card_expense_title'),
                subtitle: t('chat_card_expense_subtitle'),
                action: {
                  label: t('balancetab_view_groups'),
                  action: 'navigate',
                  target: 'balancetab',
                },
              });
              localResponse = t('chat_response_expenses');
              actions.push({
                label: t('balancetab_add_expense'),
                action: 'navigate',
                target: 'balancetab',
                icon: '\uD83D\uDCB0',
              });
              break;

            case 'article':
              cards.push({
                type: 'article',
                title: t('chat_card_article_title'),
                subtitle: t('chat_card_article_subtitle'),
                action: { label: t('home_read_article'), action: 'navigate', target: 'blog' },
              });
              localResponse = t('chat_response_article');
              actions.push({
                label: t('home_read_article'),
                action: 'navigate',
                target: 'blog',
                icon: '\uD83D\uDCD6',
              });
              break;
          }

          return { intent: ip.intent, cards, actions, localResponse };
        }
      }
    }
    return null;
  }

  private _respondWithIntent(match: IntentMatch): void {
    if (!this.conversation) return;
    const msg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: match.localResponse,
      timestamp: Date.now(),
      metadata: {
        intent: match.intent,
        cards: match.cards,
        actions: match.actions,
      },
    };
    this.conversation.messages.push(msg);
    this.conversation.updatedAt = Date.now();
    this._appendMessage(msg);
    this._scrollToBottom();
    saveConversation(this.conversation);
  }

  // ── BOS API Integration ────────────────────────────────

  private _buildContextPayload(): Record<string, unknown> {
    if (!this.ctx) return {};
    const profile = this.ctx.auth.activeProfile;
    return {
      user_name: profile?.name || this.ctx.auth.user?.name || 'User',
      profile_type: profile?.type || 'owner',
      locale: this.ctx.i18n.locale,
      current_time: new Date().toISOString(),
      is_dark_theme: this.ctx.theme.isDark,
    };
  }

  private async _queryBOS(text: string, intent?: string): Promise<void> {
    if (!this.ctx || !this.conversation) return;

    this._showTyping();
    this.isStreaming = true;
    this.abortController = new AbortController();
    this._insertThinkingBubble();

    // Wire format expected by AWENI's ChatController (multi-provider router):
    //   { message: string, session_id?: string, stream?: bool }
    // The server reads user_id from the JWT (no need to send it client-side)
    // and builds the OpenAI-style message list itself from on-disk history,
    // so we just send the latest user message + a stream flag.
    const payload = {
      message: text,
      stream: true,
      session_id: this.conversation?.id,
      // Hints for the server's analytics — ignored by Groq/Gemini providers,
      // forwarded to BOS when it's the active provider.
      context: {
        ...this._buildContextPayload(),
        intent: intent || undefined,
      },
    };

    try {
      // Try SSE streaming first.
      // BOS_CHAT_ENDPOINT is already an absolute path under /aweni/api/.
      // Concatenating ctx.apiBase (which is "/identity" for the SSO base
      // URL) produced "/identity/aweni/api/v1/chat" → 404 in production.
      // The chat endpoint lives on the AWENI API, not on Identity, so we
      // call the absolute path directly.
      const sseUrl = BOS_CHAT_ENDPOINT;
      const response = await fetch(sseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.ctx.auth.getToken()
            ? { Authorization: `Bearer ${this.ctx.auth.getToken()}` }
            : {}),
        },
        body: JSON.stringify(payload),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`BOS responded with ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('text/event-stream')) {
        // SSE streaming
        await this._handleSSEStream(response);
      } else {
        // Regular JSON response. AWENI's ResponseFormatter wraps payloads
        // in {ok:true, data:{...}, timestamp}. The actual chat answer lives
        // at data.message (provider/model/sources alongside). _parseBOSResponse
        // expects {text, metadata} for backwards compat — we adapt here.
        const envelope = await response.json();
        this._hideTyping();
        this.isStreaming = false;

        const inner = envelope?.data ?? envelope;
        const assistantMsg = this._parseBOSResponse({
          text: inner?.message ?? inner?.text ?? '',
          metadata: {
            provider: inner?.provider,
            model: inner?.model,
            sources: inner?.sources,
          },
        });
        this.conversation.messages.push(assistantMsg);
        this.conversation.updatedAt = Date.now();

        const thinkingEl = this._thinkingBubbleEl;
        this._thinkingBubbleEl = null;
        if (thinkingEl) {
          thinkingEl.dataset.msgId = assistantMsg.id;
          const contentEl = thinkingEl.querySelector('.chat-msg-content') as HTMLElement | null;
          if (contentEl) {
            contentEl.innerHTML = this._formatContent(assistantMsg.content);
            contentEl.classList.add('chat-msg-text-fadein');
          }
        } else {
          this._appendMessage(assistantMsg);
        }
        this._scrollToBottom();
        NativeService.vibrate('light');
        await saveConversation(this.conversation);
      }
    } catch (err) {
      this._hideTyping();
      this.isStreaming = false;

      if ((err as Error).name === 'AbortError') {
        this._removeThinkingBubble();
        return;
      }

      // Replace thinking bubble with error message
      const errorContent =
        this.ctx?.i18n.t('chat_error_generic') || this.ctx?.i18n.t('chat_response_default') || '';
      const thinkingEl = this._thinkingBubbleEl;
      this._thinkingBubbleEl = null;
      if (thinkingEl && this.conversation) {
        const errMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: errorContent,
          timestamp: Date.now(),
        };
        thinkingEl.dataset.msgId = errMsg.id;
        const contentEl = thinkingEl.querySelector('.chat-msg-content') as HTMLElement | null;
        if (contentEl) {
          contentEl.innerHTML = this._formatContent(errorContent);
          contentEl.classList.add('chat-msg-text-fadein');
        }
        this.conversation.messages.push(errMsg);
        this.conversation.updatedAt = Date.now();
        await saveConversation(this.conversation);
      } else if (this.conversation) {
        const fallbackMsg: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: errorContent,
          timestamp: Date.now(),
        };
        this.conversation.messages.push(fallbackMsg);
        this.conversation.updatedAt = Date.now();
        this._appendMessage(fallbackMsg);
        await saveConversation(this.conversation);
      }
      this._scrollToBottom();
      NativeService.vibrate('light');
    }
  }

  private async _handleSSEStream(response: Response): Promise<void> {
    if (!this.conversation || !response.body) return;

    const streamMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      metadata: { streaming: true },
    };
    this.conversation.messages.push(streamMsg);

    // Reuse the thinking bubble if present; otherwise append a new element
    const thinkingEl = this._thinkingBubbleEl;
    this._thinkingBubbleEl = null;
    if (thinkingEl) {
      thinkingEl.dataset.msgId = streamMsg.id;
    } else {
      this._appendMessage(streamMsg);
    }
    this._hideTyping();

    // For first-token replacement: track whether dots have been replaced yet
    let firstToken = true;
    const msgEl = thinkingEl
      ? (thinkingEl.querySelector('.chat-msg-content') as HTMLElement | null)
      : this.messagesEl?.querySelector(`[data-msg-id="${streamMsg.id}"] .chat-msg-content`);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (data === '[DONE]') break;

            try {
              const parsed = JSON.parse(data);
              // AWENI Router emits {delta:"..."} per SSE event. Older BOS
              // chunks used {token:"..."}; we accept either for forward
              // compat in case BOS becomes the active provider.
              const chunk = parsed.delta ?? parsed.token ?? '';
              if (chunk) {
                streamMsg.content += chunk;
                if (msgEl) {
                  if (firstToken) {
                    // Replace thinking dots with first text token
                    firstToken = false;
                    msgEl.innerHTML = this._formatContent(streamMsg.content);
                    msgEl.classList.add('chat-msg-text-fadein');
                  } else {
                    msgEl.innerHTML = this._formatContent(streamMsg.content);
                  }
                }
                this._scrollToBottom();
              }
              if (parsed.metadata) {
                streamMsg.metadata = { ...streamMsg.metadata, ...parsed.metadata };
              }
            } catch {
              // Plain text token
              streamMsg.content += data;
              if (msgEl) {
                if (firstToken) {
                  firstToken = false;
                  msgEl.innerHTML = this._formatContent(streamMsg.content);
                  msgEl.classList.add('chat-msg-text-fadein');
                } else {
                  msgEl.innerHTML = this._formatContent(streamMsg.content);
                }
              }
              this._scrollToBottom();
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.warn('[Chat] SSE stream error:', err);
      }
    }

    // Finalize
    streamMsg.metadata = { ...streamMsg.metadata, streaming: false };
    this.isStreaming = false;

    // Parse action markers from BOS response
    const parsed = this._parseActionMarkers(streamMsg.content);
    streamMsg.content = parsed.content;
    if (parsed.actions.length > 0) {
      streamMsg.metadata.actions = parsed.actions;
    }

    // Re-render finalized message
    this._updateMessageEl(streamMsg);
    this.conversation.updatedAt = Date.now();
    await saveConversation(this.conversation);
  }

  private _parseBOSResponse(data: {
    text?: string;
    metadata?: Record<string, unknown>;
  }): ChatMessage {
    const content = data.text || '';
    const parsed = this._parseActionMarkers(content);

    const actions: ActionButton[] = [...(parsed.actions || [])];
    const cards: PreviewCard[] = [];

    // Extract metadata actions/cards if BOS provides them
    if (data.metadata) {
      if (Array.isArray(data.metadata.actions)) {
        actions.push(...(data.metadata.actions as ActionButton[]));
      }
      if (Array.isArray(data.metadata.cards)) {
        cards.push(...(data.metadata.cards as PreviewCard[]));
      }
    }

    return {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: parsed.content,
      timestamp: Date.now(),
      metadata: {
        actions: actions.length > 0 ? actions : undefined,
        cards: cards.length > 0 ? cards : undefined,
        module: data.metadata?.module as string | undefined,
        intent: data.metadata?.intent as string | undefined,
      },
    };
  }

  private _parseActionMarkers(text: string): { content: string; actions: ActionButton[] } {
    const actions: ActionButton[] = [];
    const cleaned = text.replace(/\[ACTION:(\w+):([^\]]+)\]/g, (_match, action, target) => {
      actions.push({
        label: target.charAt(0).toUpperCase() + target.slice(1),
        action: action as ActionButton['action'],
        target,
      });
      return '';
    });
    return { content: cleaned.trim(), actions };
  }

  // ── Rendering Helpers ──────────────────────────────────

  private _renderMessages(): void {
    if (!this.messagesEl || !this.conversation) return;
    this.messagesEl.innerHTML = '';

    // Add welcome message if empty
    if (this.conversation.messages.length === 0) {
      const welcome: ChatMessage = {
        id: 'welcome',
        role: 'assistant',
        content: this.ctx!.i18n.t('chat_welcome'),
        timestamp: Date.now(),
      };
      this.conversation.messages.push(welcome);
    }

    for (const msg of this.conversation.messages) {
      this._appendMessage(msg, false);
    }
  }

  private _appendMessage(msg: ChatMessage, animate = true): void {
    if (!this.messagesEl) return;
    const el = this._createMessageEl(msg);
    if (animate) {
      el.style.animation = 'chat-fade-in var(--motion-duration-slow) var(--motion-easing-default) forwards';
    }
    this.messagesEl.appendChild(el);
  }

  private _createMessageEl(msg: ChatMessage): HTMLElement {
    const isUser = msg.role === 'user';
    const isRtl = this.ctx?.i18n.dir === 'rtl';

    const wrapper = document.createElement('div');
    wrapper.dataset.msgId = msg.id;
    wrapper.style.cssText = `
      display:flex; ${
        isUser
          ? isRtl
            ? 'flex-direction:row-reverse;'
            : 'justify-content:flex-end;'
          : 'justify-content:flex-start;'
      }
      max-width:100%;
    `;

    const bubble = document.createElement('div');
    // User bubble uses theme accent so it inverts cleanly: dark pill in light
    // mode, cream pill in dark mode. Hardcoded #0A0A0A + white text was
    // visible in light but produced a near-invisible black-on-black bubble
    // in dark since --bg-primary is also #0A0A0A in dark.
    bubble.style.cssText = `
      max-width:85%; padding:12px 16px; font-size:14px; line-height:1.55;
      ${
        isUser
          ? `background:var(--accent); color:var(--accent-text); border-radius:18px 18px ${
              isRtl ? '18px 4px' : '4px 18px'
            };`
          : `background:var(--card-bg); color:var(--text-primary); border-radius:18px 18px ${
              isRtl ? '4px 18px' : '18px 4px'
            }; border:1px solid var(--border);`
      }
      box-shadow:0 1px 3px rgba(0,0,0,0.06);
    `;

    // Content
    const contentEl = document.createElement('div');
    contentEl.className = 'chat-msg-content';
    contentEl.innerHTML = this._formatContent(msg.content);
    bubble.appendChild(contentEl);

    // Cards
    if (msg.metadata?.cards && msg.metadata.cards.length > 0) {
      const cardsContainer = document.createElement('div');
      cardsContainer.style.cssText =
        'margin-top:10px; display:flex; flex-direction:column; gap:8px;';
      for (const card of msg.metadata.cards) {
        cardsContainer.appendChild(this._createCardEl(card));
      }
      bubble.appendChild(cardsContainer);
    }

    // Action buttons
    if (msg.metadata?.actions && msg.metadata.actions.length > 0) {
      const actionsContainer = document.createElement('div');
      actionsContainer.style.cssText = 'margin-top:10px; display:flex; flex-wrap:wrap; gap:6px;';
      for (const action of msg.metadata.actions) {
        const btn = document.createElement('button');
        btn.className = 'chat-action-btn';
        btn.dataset.action = action.action;
        btn.dataset.target = action.target;
        btn.style.cssText = `
          display:inline-flex; align-items:center; gap:5px;
          padding:6px 12px; border-radius:16px; font-size:12px; font-weight:600;
          background:${
            isUser
              ? 'color-mix(in srgb, var(--accent-text) 20%, transparent)'
              : 'color-mix(in srgb, var(--accent) 12%, transparent)'
          };
          color:${isUser ? 'var(--accent-text)' : 'var(--accent)'};
          border:1px solid ${
            isUser
              ? 'color-mix(in srgb, var(--accent-text) 30%, transparent)'
              : 'color-mix(in srgb, var(--accent) 25%, transparent)'
          };
        `;
        if (action.icon) {
          const iconSpan = document.createElement('span');
          iconSpan.textContent = action.icon;
          iconSpan.style.fontSize = '14px';
          btn.appendChild(iconSpan);
        }
        const labelSpan = document.createElement('span');
        labelSpan.textContent = action.label;
        btn.appendChild(labelSpan);
        actionsContainer.appendChild(btn);
      }
      bubble.appendChild(actionsContainer);
    }

    // Timestamp
    const timeEl = document.createElement('div');
    timeEl.style.cssText = `
      font-size:11px; margin-top:6px;
      ${
        isUser
          ? 'color:color-mix(in srgb, var(--accent-text) 70%, transparent);'
          : 'color:var(--text-secondary);'
      }
      ${isUser ? 'text-align:right;' : 'text-align:left;'}
    `;
    const time = new Date(msg.timestamp);
    timeEl.textContent = time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    bubble.appendChild(timeEl);

    wrapper.appendChild(bubble);
    return wrapper;
  }

  private _createCardEl(card: PreviewCard): HTMLElement {
    const el = document.createElement('div');
    el.className = 'chat-card';
    // Per-card-type accents. The "stats" / fallback used to be #0A0A0A
    // (brand black) which produced an invisible card in dark mode (background
    // tint computed against #0A0A0A on a #0A0A0A page = no contrast). Falling
    // back to a neutral mid-gray that works in both themes.
    const colors: Record<string, string> = {
      game: '#3B82F6',
      article: '#14B8A6',
      prayer: '#F59E0B',
      expense: '#8B5CF6',
      stats: '#6B7280',
    };
    const color = colors[card.type] || '#6B7280';
    const icons: Record<string, string> = {
      game: '\uD83C\uDFAE',
      article: '\uD83D\uDCD6',
      prayer: '\uD83D\uDD4C',
      expense: '\uD83D\uDCB0',
      stats: '\uD83D\uDCCA',
    };
    const icon = icons[card.type] || '\u2728';

    el.style.cssText = `
      padding:12px; border-radius:12px;
      background:${color}10; border:1px solid ${color}30;
      animation:chat-card-appear var(--motion-duration-slow) var(--motion-easing-default);
    `;

    el.innerHTML = `
      <div style="display:flex; align-items:flex-start; gap:10px;">
        <span style="font-size:24px; flex-shrink:0;">${icon}</span>
        <div style="flex:1; min-width:0;">
          <div style="font-weight:600; font-size:13px; color:var(--text-primary);">${this._escapeHtml(
            card.title,
          )}</div>
          ${
            card.subtitle
              ? `<div style="font-size:12px; color:var(--text-secondary); margin-top:2px;">${this._escapeHtml(
                  card.subtitle,
                )}</div>`
              : ''
          }
          ${
            card.action
              ? `
            <button class="chat-action-btn" data-action="${card.action.action}" data-target="${
                  card.action.target
                }" style="
              margin-top:8px; padding:5px 12px; border-radius:14px; font-size:11px; font-weight:600;
              background:${color}; color:white; border:none;
            ">${this._escapeHtml(card.action.label)}</button>
          `
              : ''
          }
        </div>
      </div>
    `;

    return el;
  }

  private _updateMessageEl(msg: ChatMessage): void {
    if (!this.messagesEl) return;
    const existing = this.messagesEl.querySelector(`[data-msg-id="${msg.id}"]`);
    if (existing) {
      const newEl = this._createMessageEl(msg);
      existing.replaceWith(newEl);
    }
  }

  private _formatContent(text: string): string {
    let formatted = this._escapeHtml(text);
    // Bold: **text**
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    // Italic: *text*
    formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // Links: [text](url)
    formatted = formatted.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener" style="color:var(--accent);text-decoration:underline;">$1</a>',
    );
    // Line breaks
    formatted = formatted.replace(/\n/g, '<br>');
    return formatted;
  }

  private _escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ── Conversation Management ────────────────────────────

  private _createNewConversation(): Conversation {
    return {
      id: crypto.randomUUID(),
      title: this.ctx?.i18n.t('chat_new_conversation') || 'New conversation',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
  }

  private async _startNewConversation(): Promise<void> {
    // Save current conversation
    if (this.conversation && this.conversation.messages.length > 1) {
      await saveConversation(this.conversation);
    }

    this._cancelStreaming();
    this.conversation = this._createNewConversation();
    this.conversations = await loadConversations();
    this.conversations.unshift(this.conversation);
    this._render();
    this._bindEvents();
    this.inputEl?.focus();
  }

  // ── History Panel ──────────────────────────────────────

  private _toggleHistory(): void {
    if (this._historyOpen) {
      this._closeHistory();
    } else {
      this._openHistory();
    }
  }

  private async _openHistory(): Promise<void> {
    this._historyOpen = true;
    this.conversations = await loadConversations();
    this._renderHistoryList();

    const overlay = this.container?.querySelector<HTMLElement>('#chat-history-overlay');
    const panel = this.historyPanelEl;
    if (overlay) overlay.style.display = 'block';
    requestAnimationFrame(() => {
      if (panel) panel.style.transform = 'translateX(0)';
      if (overlay) overlay.style.opacity = '1';
    });
  }

  private _closeHistory(): void {
    this._historyOpen = false;
    const isRtl = this.ctx?.i18n.dir === 'rtl';
    const overlay = this.container?.querySelector<HTMLElement>('#chat-history-overlay');
    const panel = this.historyPanelEl;
    if (panel) panel.style.transform = `translateX(${isRtl ? '100%' : '-100%'})`;
    if (overlay) overlay.style.opacity = '0';
    setTimeout(() => {
      if (overlay) overlay.style.display = 'none';
    }, 300);
  }

  private _renderHistoryList(): void {
    const list = this.container?.querySelector<HTMLElement>('#chat-history-list');
    if (!list) return;

    if (this.conversations.length === 0) {
      list.innerHTML = `<div style="padding:24px; text-align:center; color:var(--text-secondary); font-size:13px;">
        ${this.ctx?.i18n.t('chat_no_history') || 'No conversations yet'}
      </div>`;
      return;
    }

    list.innerHTML = this.conversations
      .slice(0, MAX_HISTORY_DISPLAY)
      .map((conv) => {
        const isActive = conv.id === this.conversation?.id;
        const date = new Date(conv.updatedAt);
        const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const msgCount = conv.messages.filter((m) => m.role !== 'system').length;

        return `
        <div class="chat-history-item" data-conv-id="${conv.id}" style="
          padding:12px; border-radius:10px; margin-bottom:4px;
          ${
            isActive
              ? 'background:color-mix(in srgb, var(--accent) 14%, transparent); border:1px solid color-mix(in srgb, var(--accent) 30%, transparent);'
              : 'background:transparent; border:1px solid transparent;'
          }
        ">
          <div style="display:flex; justify-content:space-between; align-items:flex-start; gap:8px;">
            <div style="flex:1; min-width:0;">
              <div style="font-size:13px; font-weight:${
                isActive ? '600' : '500'
              }; color:var(--text-primary); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
                ${this._escapeHtml(conv.title)}
              </div>
              <div style="font-size:11px; color:var(--text-secondary); margin-top:3px;">
                ${dateStr} &middot; ${
          this.ctx?.i18n.t('chat_history_msg_count', { count: msgCount }) || `${msgCount} msgs`
        }
              </div>
            </div>
            <button class="chat-history-delete" data-delete-id="${conv.id}" style="
              width:24px; height:24px; border-radius:6px; border:none;
              background:transparent; color:var(--text-secondary); cursor:pointer;
              display:flex; align-items:center; justify-content:center;
              opacity:0.5; transition:opacity var(--motion-duration-fast);
            " aria-label="${this.ctx?.i18n.t('chat_delete') || 'Delete'}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </button>
          </div>
        </div>
      `;
      })
      .join('');

    // Bind clicks
    list.querySelectorAll<HTMLElement>('.chat-history-item').forEach((item) => {
      item.addEventListener('click', (e) => {
        const deleteBtn = (e.target as HTMLElement).closest('.chat-history-delete');
        if (deleteBtn) {
          e.stopPropagation();
          const id = (deleteBtn as HTMLElement).dataset.deleteId;
          if (id) this._deleteConversation(id);
          return;
        }
        const convId = item.dataset.convId;
        if (convId) this._switchConversation(convId);
      });
    });
  }

  private async _switchConversation(convId: string): Promise<void> {
    // Save current
    if (this.conversation && this.conversation.messages.length > 1) {
      await saveConversation(this.conversation);
    }

    const target = this.conversations.find((c) => c.id === convId);
    if (!target) return;

    this._cancelStreaming();
    this.conversation = target;
    this._closeHistory();
    this._render();
    this._bindEvents();
    this._scrollToBottom(false);
  }

  private async _deleteConversation(convId: string): Promise<void> {
    await deleteConversation(convId);
    this.conversations = this.conversations.filter((c) => c.id !== convId);

    if (this.conversation?.id === convId) {
      if (this.conversations.length > 0) {
        this.conversation = this.conversations[0];
      } else {
        this.conversation = this._createNewConversation();
      }
      this._render();
      this._bindEvents();
    }

    this._renderHistoryList();
  }

  // ── Action Handling ────────────────────────────────────

  private _handleAction(action: string, target: string): void {
    if (!this.ctx) return;
    switch (action) {
      case 'navigate':
        this.ctx.navigate(target);
        break;
      case 'copy':
        navigator.clipboard?.writeText(target);
        this._showToast(this.ctx.i18n.t('chat_copied') || 'Copied!');
        break;
      case 'share':
        if (navigator.share) {
          navigator.share({ text: target }).catch(() => {});
        }
        break;
      case 'api_call':
        // Future: trigger API actions
        break;
    }
  }

  // ── UI Helpers ─────────────────────────────────────────

  private _showTyping(): void {
    if (this.typingEl) this.typingEl.style.display = 'block';
    this._scrollToBottom();
  }

  private _hideTyping(): void {
    if (this.typingEl) this.typingEl.style.display = 'none';
  }

  /** Insert an assistant bubble with animated thinking dots immediately on send. */
  private _insertThinkingBubble(): void {
    if (!this.messagesEl || !this.ctx) return;
    const isRtl = this.ctx.i18n.dir === 'rtl';
    const wrapper = document.createElement('div');
    wrapper.dataset.msgId = `thinking-${Date.now()}`;
    wrapper.style.cssText = `display:flex;justify-content:flex-start;max-width:100%;`;
    const bubble = document.createElement('div');
    bubble.style.cssText = `
      max-width:85%;padding:12px 16px;font-size:14px;line-height:1.55;
      background:var(--card-bg);color:var(--text-primary);
      border-radius:18px 18px ${isRtl ? '4px 18px' : '18px 4px'};
      border:1px solid var(--border);box-shadow:0 1px 3px rgba(0,0,0,0.06);
      min-height:44px;contain:layout;
    `;
    const contentEl = document.createElement('div');
    contentEl.className = 'chat-msg-content';
    contentEl.innerHTML =
      '<span class="aweni-thinking-dots" aria-label="thinking"><span></span><span></span><span></span></span>';
    bubble.appendChild(contentEl);
    wrapper.appendChild(bubble);
    wrapper.style.animation = 'chat-fade-in var(--motion-duration-slow) var(--motion-easing-default) forwards';
    this.messagesEl.appendChild(wrapper);
    this._thinkingBubbleEl = wrapper;
    this._scrollToBottom();
  }

  /** Remove thinking bubble without replacing it (e.g. on AbortError). */
  private _removeThinkingBubble(): void {
    if (this._thinkingBubbleEl) {
      this._thinkingBubbleEl.remove();
      this._thinkingBubbleEl = null;
    }
  }

  private _scrollToBottom(smooth = true): void {
    if (!this.messagesEl) return;
    if (smooth) {
      requestAnimationFrame(() => {
        this.messagesEl!.scrollTop = this.messagesEl!.scrollHeight;
      });
    } else {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    }
  }

  private _autoResize(): void {
    if (!this.inputEl) return;
    this.inputEl.style.height = 'auto';
    this.inputEl.style.height = Math.min(this.inputEl.scrollHeight, 120) + 'px';
  }

  private _cancelStreaming(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isStreaming = false;
    this._hideTyping();
  }

  private _showToast(message: string): void {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position:fixed; bottom:100px; left:50%; transform:translateX(-50%);
      padding:10px 20px; border-radius:20px; font-size:13px; font-weight:500;
      background:var(--text-primary); color:var(--bg-primary);
      box-shadow:0 4px 12px rgba(0,0,0,0.15); z-index:999;
      animation:chat-fade-in var(--motion-duration-slow) var(--motion-easing-default);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity var(--motion-duration-slow) var(--motion-easing-default)';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }
}
